import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import { Client } from '@app/types/api';
import { Flex, Tabs, theme, Typography } from '@app/ui';
import { BroadcastTab } from '../utils/broadcastsUtils';
import {
    VERBAL_MENTIONS_TRACKING,
    BROADCAST_IMPRESSIONS,
    BROADCAST_DEMOGRAPHICS,
} from '@app/constants/config';
import { optimizely } from '@app/utils/optimizely/config';

type NavTabsProps = {
    activeTab: BroadcastTab;
    onTabSelection: (tab: BroadcastTab) => void;
    client: Client;
};

const BROADCAST_TABS = [
    {
        name: BroadcastTab.EXPOSURES,
        titleKey: 'broadcastExposure.tabs.sampleExposures',
        icon: props => <i className="fa-regular fa-photo-video" {...props} />,
        permission: null,
    },
    {
        name: BroadcastTab.VIEWERSHIP,
        titleKey: 'broadcastExposure.tabs.viewership',
        icon: props => <i className="fa-regular fa-chart-bar" {...props} />,
        permission: BROADCAST_IMPRESSIONS,
    },
    {
        name: BroadcastTab.DEMOGRAPHICS,
        titleKey: 'broadcastExposure.tabs.demographics',
        icon: props => <i className="fa-regular fa-users" {...props} />,
        permission: BROADCAST_DEMOGRAPHICS,
    },
    {
        name: BroadcastTab.VERBAL_MENTIONS,
        titleKey: 'broadcastExposure.tabs.verbalMentions',
        icon: props => <i className="fa-regular fa-bullhorn" {...props} />,
        permission: VERBAL_MENTIONS_TRACKING,
    },
];

const BroadcastTabs = ({ client, activeTab, onTabSelection }: NavTabsProps) => {
    const { t } = useTranslation('measurement');
    const [hoveredTab, setHoveredTab] = useState(null);
    const [isVMBroadcastExposureEnabled, setIsVMBroadcastExposureEnabled] =
        useState(false);

    useEffect(() => {
        (async () => {
            const isVMEnabled = await optimizely
                .onReady()
                .then(() =>
                    optimizely.isFeatureEnabled('vm-broadcast-exposure'),
                );

            setIsVMBroadcastExposureEnabled(isVMEnabled);
        })();
    }, []);

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const items = useMemo(() => {
        return Object.values(BROADCAST_TABS)
            .filter(({ permission, name }) => {
                const isAllowed =
                    !permission || client.permissions.includes(permission);
                const isVisible =
                    name !== BroadcastTab.VERBAL_MENTIONS ||
                    isVMBroadcastExposureEnabled;
                return isAllowed && isVisible;
            })
            .map(({ name, titleKey, icon }) => {
                const isActive = activeTab === name;
                const isHovered = hoveredTab === name;

                const iconComponent = React.createElement(icon, {
                    color: isActive || isHovered ? colorPrimary : '#515252',
                    width: 16,
                    height: 16,
                    style: { fontSize: 16 },
                });

                const tabComponent = (
                    <div
                        onMouseEnter={() => setHoveredTab(name)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        {iconComponent}
                        <Typography.Text
                            strong={false}
                            style={{
                                color:
                                    isActive || isHovered
                                        ? colorPrimary
                                        : '#515252',
                            }}
                        >
                            {t(titleKey)}
                        </Typography.Text>
                    </div>
                );

                return {
                    key: name,
                    label: tabComponent,
                };
            })
            .filter(Boolean);
    }, [
        client.permissions,
        activeTab,
        hoveredTab,
        isVMBroadcastExposureEnabled,
        t,
    ]);

    return (
        <Flex flex={1} style={{ width: '100%' }}>
            <Tabs
                activeKey={activeTab}
                items={items}
                onChange={onTabSelection}
            />
            <div
                style={{
                    marginBottom: 16,
                    borderBottom: '1px solid #BFBFBF',
                    width: '100%',
                }}
            >
                {''}
            </div>
        </Flex>
    );
};

export default connect(['client'])(BroadcastTabs);
